import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserActivityDashboard } from '@/hooks/useUserActivityDashboard';
import { DART_LOGO, ROUTES } from '@/config/constant';
import { SimpleBarChart } from './charts/SimpleBarChart';
import { SimpleLineChart } from './charts/SimpleLineChart';
import { SimpleDataTable } from './charts/SimpleDataTable';
import { SummaryCards } from './charts/SummaryCards';
import useUserActivityDashboardStyles from './userActivityDashboard.styles';
import {
  PAGE_TITLE,
  DAILY_ACTIVE_USERS_COLUMNS,
  TOP_USERS_COLUMNS,
  PARTIAL_RESULTS_WARNING,
} from './userActivityDashboard.constants';

/** User Activity Dashboard — admin-only page fed by the CloudWatch analytics Lambda. */
const UserActivityDashboard: React.FC = () => {
  const classes = useUserActivityDashboardStyles();
  const navigate = useNavigate();
  const { isLoading: authLoading, canManageUsers } = useAuth();

  const {
    // Data
    widgets,
    metadata,
    // UI state
    loading,
    error,
    // Date range state
    startDate,
    endDate,
    today,
    minDateStr,
    // Handlers
    handleStartDateChange,
    handleEndDateChange,
    handleRefresh,
    handleBackToDashboard,
    clearError,
  } = useUserActivityDashboard();

  // Wait for role resolution before making an access decision
  if (authLoading) {
    return (
      <Box className={classes.centeredBox} role="status" aria-live="polite">
        <CircularProgress size={28} aria-label="Loading authentication status" />
      </Box>
    );
  }

  // Admin-only guard — redirect non-admins back to the default dashboard
  if (!canManageUsers) {
    navigate(ROUTES.HOME, { replace: true });
    return null;
  }

  return (
    <Box className={classes.page}>
      {/* Header bar */}
      <Box className={classes.headerBar}>
        <Tooltip title="Back to Dashboard">
          <IconButton 
            onClick={handleBackToDashboard} 
            size="small" 
            aria-label="Back to Dashboard"
            className={classes.headerIconButton}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <img src={DART_LOGO} alt="DART Logo" className={classes.logo} />
        <Typography variant="h6" component="h1" className={classes.divider}>|</Typography>
        <Typography variant="h6" component="h1" className={classes.titleText}>{PAGE_TITLE}</Typography>

        <Box className={classes.spacer} />

        <TextField
          type="date"
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          size="small"
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              min: minDateStr,
              max: today,
              'aria-label': 'Select start date for data range',
            },
          }}
          sx={{ minWidth: 150, mr: 1 }}
        />

        <TextField
          type="date"
          label="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          size="small"
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: {
              min: startDate,
              max: today,
              'aria-label': 'Select end date for data range',
            },
          }}
          sx={{ minWidth: 150, mr: 1 }}
        />

        <Tooltip title="Refresh">
          <span>
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading} 
              size="small" 
              aria-label="Refresh data"
              className={classes.headerIconButton}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        {loading && (
          <Box className={classes.centeredBox} role="status" aria-live="polite">
            <CircularProgress aria-label="Loading user activity data" />
          </Box>
        )}

        {!loading && error && (
          <Alert 
            severity="error" 
            className={classes.errorAlert}
            role="alert"
            aria-live="assertive"
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && widgets && (
          <Box className={classes.widgetStack}>
            {metadata?.partialResults && (
              <Alert 
                severity="warning" 
                role="alert" 
                aria-live="polite"
              >
                {PARTIAL_RESULTS_WARNING}
              </Alert>
            )}

            <SummaryCards cards={widgets.summary.cards} />

            {/* totalApiCallsPerDay data rendered as a line chart to match CloudWatch's native view */}
            <Paper variant="outlined" className={classes.widgetPaper}>
              <SimpleLineChart
                title={widgets.totalApiCallsPerDay.title}
                labels={widgets.totalApiCallsPerDay.labels}
                values={widgets.totalApiCallsPerDay.values}
              />
            </Paper>

            {/* Three-column row: Weekly Active Users Trend, Daily Active Users, Top Users */}
            <Box className={classes.threeColumnRow}>
              <Paper variant="outlined" className={`${classes.widgetPaper} ${classes.threeColumnItem}`}>
                <SimpleBarChart
                  title={widgets.weeklyActiveUsersTrend.title}
                  labels={widgets.weeklyActiveUsersTrend.labels}
                  values={widgets.weeklyActiveUsersTrend.values}
                  height={300}
                />
              </Paper>

              <Paper variant="outlined" className={`${classes.widgetPaper} ${classes.threeColumnItem}`}>
                <SimpleDataTable
                  title={widgets.dailyActiveUsers.title}
                  columns={[...DAILY_ACTIVE_USERS_COLUMNS]}
                  rows={widgets.dailyActiveUsers.labels.map((day, index) => ({
                    day,
                    unique_users: widgets.dailyActiveUsers.values[index] ?? 0,
                  }))}
                  maxHeight={280}
                />
              </Paper>

              <Paper variant="outlined" className={`${classes.widgetPaper} ${classes.threeColumnItem}`}>
                <SimpleDataTable
                  title={widgets.topUsers.title}
                  columns={[...TOP_USERS_COLUMNS]}
                  rows={widgets.topUsers.labels.map((email, index) => ({
                    user_email: email,
                    api_calls: widgets.topUsers.values[index] ?? 0,
                  }))}
                  maxHeight={280}
                />
              </Paper>
            </Box>

            <Paper variant="outlined" className={classes.widgetPaper}>
              <SimpleDataTable
                title={widgets.apiCallsPerUserPerDay.title}
                columns={widgets.apiCallsPerUserPerDay.columns}
                rows={widgets.apiCallsPerUserPerDay.rows}
              />
            </Paper>

            {metadata && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                component="footer"
                role="contentinfo"
              >
                {metadata.timeRange} · Queried by {metadata.queriedBy} at {new Date(metadata.queryTime).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserActivityDashboard;
